<script setup lang="ts">
import { ref, type PropType, computed } from 'vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import FlowStepsSection, {
	type FlowStep,
} from '@/components/ui/FlowStepsSection.vue'

export interface FlowStepTab {
	id: string
	title: string
	steps: Omit<FlowStep, 'icon'>[]
}

export interface TabbedFlowSectionContent {
	sectionTitle: string
	buttonText: string
	buttonLink: string
	tabs: FlowStepTab[]
}

const emit = defineEmits(['tab-click', 'button-click'])

const props = defineProps({
	content: {
		type: Object as PropType<TabbedFlowSectionContent>,
		required: true,
	},
	flowIcons: {
		type: Object as PropType<Record<string, string[]>>,
		required: true,
	},
})

const activeTab = ref(0)
const tabs = computed(() => props.content.tabs)
const currentTab = computed(() => tabs.value[activeTab.value])

const combinedSteps = computed(() => {
	const tabId = currentTab.value.id
	const icons = props.flowIcons[tabId] || []

	return currentTab.value.steps.map((step, index) => ({
		...step,
		icon: icons[index] || '',
	}))
})

const handleTabClick = (index: number) => {
	activeTab.value = index

	emit('tab-click', index)
}

const handleButtonClick = () => {
	emit('button-click')
}
</script>

<template>
	<div class="flex flex-col items-center">
		<div
			class="typography-h2-str mb-acquaintances text-center [&>span]:text-o-500"
			v-html="content.sectionTitle"
		></div>

		<BaseTabs
			v-if="tabs.length > 1"
			size="large"
			type="secondary"
			class="mb-48px"
		>
			<BaseTabItem
				v-for="(tab, index) in tabs"
				:key="index"
				type="secondary"
				size="large"
				:isSelected="index === activeTab"
				@click="handleTabClick(index)"
			>
				{{ tab.title }}
			</BaseTabItem>
		</BaseTabs>

		<FlowStepsSection :steps="combinedSteps" />

		<BaseButton
			size="large"
			class="mx-auto mt-acquaintances flex"
			@click="handleButtonClick"
		>
			{{ content.buttonText }}
		</BaseButton>
	</div>
</template>
